import { Component, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from '../auth/auth.service';
import { ThemeService } from '../theme/theme.service';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [CommonModule, AvatarModule, TooltipModule],
  template: `
    <header class="topbar">
      <div class="topbar__left">
        <img
          [src]="theme.isDark() ? 'images/icon-mobil-dark.svg' : 'images/icon-mobil-ligth.svg'"
          alt="Peru Money"
          class="topbar__mobile-logo"
        />
        <button class="topbar__menu-btn" (click)="menuToggle.emit()">
          <i class="pi pi-bars"></i>
        </button>
      </div>

      <div class="topbar__right">
        <button
          class="topbar__theme-btn"
          [class.topbar__theme-btn--light]="!theme.isDark()"
          (click)="theme.toggle()"
          [pTooltip]="theme.isDark() ? 'Modo claro' : 'Modo oscuro'"
        >
          <i [class]="theme.isDark() ? 'pi pi-sun' : 'pi pi-moon'"></i>
        </button>

        <div class="topbar__user" (click)="menuOpen = !menuOpen">
          @if (auth.user()?.photo) {
            <img [src]="auth.user()!.photo" alt="" class="topbar__photo" />
          } @else {
            <p-avatar
              [label]="userInitials"
              shape="circle"
              styleClass="topbar__avatar"
            />
          }
          <div class="topbar__user-info">
            <span class="topbar__user-name">{{ auth.user()?.is_agency ? auth.user()?.agency_name : auth.userDisplayName() }}</span>
            <span class="topbar__user-role">{{ auth.user()?.is_agency ? auth.user()?.role : auth.user()?.area_name }}</span>
          </div>
          <i class="pi pi-chevron-down topbar__chevron" [class.topbar__chevron--open]="menuOpen"></i>
        </div>

        <!-- Custom dropdown menu -->
        @if (menuOpen) {
          <div class="user-menu-backdrop" (click)="menuOpen = false"></div>
          <div class="user-menu">
            <!-- User card header -->
            <div class="user-menu__header">
              @if (auth.user()?.photo) {
                <img [src]="auth.user()!.photo" alt="" class="user-menu__photo" />
              } @else {
                <div class="user-menu__avatar">{{ userInitials }}</div>
              }
              <div class="user-menu__info">
                <span class="user-menu__name">{{ auth.userDisplayName() }}</span>
                <span class="user-menu__detail">{{ auth.user()?.role }}</span>
              </div>
            </div>
            <!-- Details -->
            <div class="user-menu__details">
              @if (auth.user()?.area_name) {
                <div class="user-menu__detail-row">
                  <i class="pi pi-briefcase"></i>
                  <span>{{ auth.user()!.area_name }}</span>
                </div>
              }
              @if (auth.user()?.agency_name) {
                <div class="user-menu__detail-row">
                  <i class="pi pi-building"></i>
                  <span>{{ auth.user()!.agency_name }}</span>
                </div>
              }
              @if (auth.user()?.username) {
                <div class="user-menu__detail-row">
                  <i class="pi pi-at"></i>
                  <span>{{ auth.user()!.username }}</span>
                </div>
              }
            </div>
            <!-- Actions -->
            <div class="user-menu__actions">
              <button class="user-menu__btn" (click)="goToProfile()">
                <i class="pi pi-user"></i>
                <span>Mi Perfil</span>
              </button>
              <button class="user-menu__btn user-menu__btn--danger" (click)="logout()">
                <i class="pi pi-sign-out"></i>
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        }
      </div>
    </header>
  `,
  styles: [`
    .topbar {
      position: fixed;
      top: 0;
      right: 0;
      left: 0;
      height: 52px;
      background: rgba(255, 255, 255, 0.85);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-bottom: 1px solid var(--surface-border);
      border-top: 3px solid #ef7c0a;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 1.5rem;
      z-index: 99;
    }

    .topbar__left {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    .topbar__menu-btn {
      display: none;
      background: none;
      border: none;
      font-size: 1.25rem;
      color: var(--text-color);
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 8px;
      transition: background 0.15s;
    }
    .topbar__menu-btn:hover {
      background: var(--surface-hover);
    }
    .topbar__mobile-logo {
      display: none;
      height: 28px;
      width: auto;
    }

    .topbar__theme-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 1px solid var(--surface-border);
      background: var(--surface-ground);
      color: var(--text-color);
      cursor: pointer;
      font-size: 1rem;
      transition: background 0.15s, color 0.15s;
    }
    .topbar__theme-btn:hover {
      background: var(--surface-hover);
    }
    .topbar__theme-btn--light {
      color: #c8a205;
    }
    .topbar__theme-btn--light:hover {
      color: #a88600;
    }
    :host-context(.dark-mode) .topbar__theme-btn {
      color: #e6b800;
    }
    :host-context(.dark-mode) .topbar__theme-btn:hover {
      color: #ffd000;
    }

    .topbar__right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      position: relative;
    }

    .topbar__user {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      padding: 0.375rem 0.75rem;
      border-radius: 10px;
      cursor: pointer;
      transition: background 0.15s;
    }
    .topbar__user:hover {
      background: var(--surface-hover);
    }

    .topbar__photo {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid var(--surface-border);
    }
    :host ::ng-deep .topbar__avatar {
      background: #25d07b !important;
      color: white !important;
      font-weight: 700;
      font-size: 0.8rem;
    }

    .topbar__user-info {
      display: flex;
      flex-direction: column;
    }
    .topbar__user-name {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--text-color);
      line-height: 1.2;
    }
    .topbar__user-role {
      font-size: 0.6875rem;
      color: var(--text-color-secondary);
    }
    .topbar__chevron {
      font-size: 0.625rem;
      color: var(--text-color-secondary);
      transition: transform 0.2s;
    }
    .topbar__chevron--open {
      transform: rotate(180deg);
    }

    /* Custom user dropdown */
    .user-menu-backdrop {
      position: fixed;
      inset: 0;
      z-index: 199;
    }

    .user-menu {
      position: absolute;
      top: calc(100% + 0.5rem);
      right: 0;
      width: 280px;
      background: var(--surface-card, #fff);
      border: 1px solid var(--surface-border);
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
      z-index: 200;
      overflow: hidden;
      animation: menuSlideIn 0.15s ease-out;
    }

    @keyframes menuSlideIn {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .user-menu__header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 1rem 1.125rem;
      background: linear-gradient(135deg, rgba(37, 208, 123, 0.08), rgba(37, 208, 123, 0.03));
      border-bottom: 1px solid var(--surface-border);
    }
    .user-menu__photo {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid #25d07b;
    }
    .user-menu__avatar {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: #25d07b;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 1rem;
      flex-shrink: 0;
    }
    .user-menu__info {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }
    .user-menu__name {
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--text-color);
      line-height: 1.3;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .user-menu__detail {
      font-size: 0.75rem;
      color: #25d07b;
      font-weight: 500;
    }

    .user-menu__details {
      padding: 0.75rem 1.125rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      border-bottom: 1px solid var(--surface-border);
    }
    .user-menu__detail-row {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      font-size: 0.8125rem;
      color: var(--text-color-secondary);
    }
    .user-menu__detail-row i {
      font-size: 0.875rem;
      width: 1rem;
      text-align: center;
      color: var(--text-color-secondary);
      opacity: 0.7;
    }

    .user-menu__actions {
      padding: 0.5rem;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .user-menu__btn {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
      padding: 0.625rem 0.75rem;
      border: none;
      background: none;
      border-radius: 8px;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--text-color);
      cursor: pointer;
      transition: background 0.15s;
      font-family: inherit;
    }
    .user-menu__btn:hover {
      background: var(--surface-hover, #f1f5f9);
    }
    .user-menu__btn i {
      font-size: 1rem;
      width: 1.25rem;
      text-align: center;
    }
    .user-menu__btn--danger {
      color: #ef4444;
    }
    .user-menu__btn--danger:hover {
      background: rgba(239, 68, 68, 0.08);
    }

    :host-context(.dark-mode) .topbar {
      background: rgba(26, 26, 26, 0.9);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }
    :host-context(.dark-mode) .user-menu {
      background: #1e1e1e;
      border-color: #333;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
    }
    :host-context(.dark-mode) .user-menu__header {
      background: linear-gradient(135deg, rgba(37, 208, 123, 0.12), rgba(37, 208, 123, 0.04));
      border-bottom-color: #333;
    }
    :host-context(.dark-mode) .user-menu__name {
      color: #e2e8f0;
    }
    :host-context(.dark-mode) .user-menu__details {
      border-bottom-color: #333;
    }
    :host-context(.dark-mode) .user-menu__detail-row {
      color: #94a3b8;
    }
    :host-context(.dark-mode) .user-menu__detail-row i {
      color: #94a3b8;
    }
    :host-context(.dark-mode) .user-menu__btn {
      color: #e2e8f0;
    }
    :host-context(.dark-mode) .user-menu__btn:hover {
      background: rgba(255, 255, 255, 0.08);
    }
    :host-context(.dark-mode) .user-menu__btn--danger {
      color: #f87171;
    }
    :host-context(.dark-mode) .user-menu__btn--danger:hover {
      background: rgba(248, 113, 113, 0.1);
    }

    @media (max-width: 991px) {
      .topbar {
        box-shadow: 0 1px 4px rgba(0,0,0,0.08);
      }
      .topbar__menu-btn { display: block; }
      .topbar__mobile-logo {
        display: block;
      }
      .topbar__user-info { display: none; }
      .topbar__chevron { display: none; }
    }
  `],
})
export class TopbarComponent {
  @Output() menuToggle = new EventEmitter<void>();

  auth = inject(AuthService);
  theme = inject(ThemeService);
  private router = inject(Router);

  menuOpen = false;

  get userInitials(): string {
    const user = this.auth.user();
    if (!user) return '?';
    const first = user.user_firstname?.[0] || user.username[0] || '';
    const last = user.user_lastname?.[0] || '';
    return (first + last).toUpperCase();
  }

  goToProfile(): void {
    this.menuOpen = false;
    this.router.navigate(['/profile']);
  }

  logout(): void {
    this.menuOpen = false;
    this.auth.logout();
  }
}
